import { create } from 'zustand'
import * as Sentry from '@sentry/react'
import api from '../services/api'
import { trackEvent } from '../services/tracking'
import { getUtmData } from '../services/utm'
import { hasAnalytiqueConsent, syncMarketingConsent } from '../services/consent'
import { useConsentStore } from './useConsentStore'

// Nettoyage one-shot de l'ancien localStorage (migration cookie httpOnly)
localStorage.removeItem('bonurba-auth')

// --- Refresh proactif : timer + visibilité onglet ---

let refreshTimer = null

// Après un échec transitoire, réessayer sans attendre l'expiration : la
// valeur passe par le plancher de 30 s de `scheduleProactiveRefresh`.
const REESSAI_REFRESH_S = 0

function scheduleProactiveRefresh(expiresInSeconds, refreshFn) {
  clearTimeout(refreshTimer)
  // Rafraîchir 2 minutes avant expiration (minimum 30s pour éviter boucle)
  const delayMs = Math.max((expiresInSeconds - 120) * 1000, 30000)
  refreshTimer = setTimeout(() => {
    // Ne rafraîchir que si l'onglet est visible : en arrière-plan le
    // navigateur bride les minuteries, et un refresh y sert à rien.
    if (document.visibilityState === 'visible') {
      refreshFn()
      return
    }
    // Onglet masqué : reprogrammer, sinon la chaîne de refresh meurt ici
    // et plus rien ne la relance. `visibilitychange` finit par rattraper
    // le coup, mais seulement si l'utilisateur revient sur l'onglet — un
    // onglet resté masqué ne se rafraîchissait plus jamais.
    scheduleProactiveRefresh(expiresInSeconds, refreshFn)
  }, delayMs)
}

function clearProactiveRefresh() {
  clearTimeout(refreshTimer)
  refreshTimer = null
}

const useAuthStore = create((set, get) => ({
  user: null,
  accessToken: null,
  isAuthenticated: false,
  isInitializing: true,
  sessionExpired: false,

  /**
   * Restaure la session via le refresh token cookie httpOnly.
   * Appelé une seule fois au montage de l'application.
   */
  initializeAuth: async () => {
    // Pas de cookie indicateur → pas de session, inutile d'appeler /refresh
    if (!document.cookie.includes('bonurba_logged_in')) {
      set({ isInitializing: false })
      return
    }
    try {
      const { data } = await api.post('/api/auth/refresh')
      set({ accessToken: data.access_token, isAuthenticated: true, sessionExpired: false })
      api.defaults.headers.common['Authorization'] = `Bearer ${data.access_token}`
      scheduleProactiveRefresh(data.expires_in ?? 900, get().refreshAccessToken)
      const { data: user } = await api.get('/api/auth/me')
      set({ user, isInitializing: false })
      if (hasAnalytiqueConsent()) Sentry.setUser({ id: user.id, email: user.email })
      // Synchroniser le consentement marketing localStorage → backend
      const { marketing } = useConsentStore.getState()
      if (marketing !== null) syncMarketingConsent(marketing)
    } catch {
      set({ isInitializing: false })
    }
  },

  login: async (email, password) => {
    const { data } = await api.post('/api/auth/login', { email, password })
    set({
      accessToken: data.access_token,
      isAuthenticated: true,
      sessionExpired: false,
    })
    api.defaults.headers.common['Authorization'] = `Bearer ${data.access_token}`
    scheduleProactiveRefresh(data.expires_in ?? 900, get().refreshAccessToken)
    // Récupérer les infos utilisateur
    const { data: user } = await api.get('/api/auth/me')
    set({ user })
    if (hasAnalytiqueConsent()) Sentry.setUser({ id: user.id, email: user.email })
    trackEvent('auth.login', { user_id: user.id })
    // Synchroniser le consentement marketing localStorage → backend
    const { marketing } = useConsentStore.getState()
    if (marketing !== null) syncMarketingConsent(marketing)
  },

  register: async (userData) => {
    const utmData = getUtmData()
    const payload = { ...userData }
    // Ne pas envoyer segment si non renseigné (chaîne vide → null)
    if (!payload.segment) {
      payload.segment = null
    }
    if (utmData) {
      payload.utm_data = utmData
    }
    const { data } = await api.post('/api/auth/register', payload)
    // Auto-login : stocker le token et hydrater le profil
    set({ accessToken: data.access_token, isAuthenticated: true, sessionExpired: false })
    api.defaults.headers.common['Authorization'] = `Bearer ${data.access_token}`
    scheduleProactiveRefresh(data.expires_in ?? 900, get().refreshAccessToken)
    const { data: user } = await api.get('/api/auth/me')
    set({ user })
    if (hasAnalytiqueConsent()) Sentry.setUser({ id: user.id, email: user.email })
    trackEvent('auth.register', { user_id: user.id })
    // Synchroniser le consentement marketing localStorage → backend
    const { marketing } = useConsentStore.getState()
    if (marketing !== null) syncMarketingConsent(marketing)
  },

  updateProfile: async (data) => {
    const { user } = get()
    const { data: updated } = await api.put(`/api/users/${user.id}`, data)
    set({ user: { ...user, ...updated } })
    trackEvent('account.profile_update')
    return updated
  },

  changePassword: async (currentPassword, newPassword) => {
    const { user } = get()
    const { data } = await api.put(`/api/users/${user.id}/change-password`, {
      current_password: currentPassword,
      new_password: newPassword,
    })
    trackEvent('account.password_change')
    return data
  },

  logout: async () => {
    clearProactiveRefresh()
    try {
      await api.post('/api/auth/logout')
    } catch {
      // Ignorer si le token est déjà expiré
    }
    trackEvent('auth.logout')
    if (hasAnalytiqueConsent()) Sentry.setUser(null)
    set({ user: null, accessToken: null, isAuthenticated: false, sessionExpired: false })
    delete api.defaults.headers.common['Authorization']
  },

  refreshAccessToken: async () => {
    try {
      const { data } = await api.post('/api/auth/refresh')
      set({ accessToken: data.access_token, sessionExpired: false })
      api.defaults.headers.common['Authorization'] = `Bearer ${data.access_token}`
      scheduleProactiveRefresh(data.expires_in ?? 900, get().refreshAccessToken)
      return true
    } catch (error) {
      // Seul un refus d'authentification met fin à la session : le backend
      // rend 401 (cookie absent) ou 400 (refresh token expiré ou invalide).
      // Tout le reste — panne réseau, 429 de rafale, 502/503 passager — est
      // transitoire. Les confondre déconnectait l'utilisateur sur un hoquet
      // du réseau, écran « Votre session a expiré » à l'appui, alors que son
      // refresh token restait valable sept jours.
      const status = error?.response?.status
      if (status !== 400 && status !== 401) {
        scheduleProactiveRefresh(REESSAI_REFRESH_S, get().refreshAccessToken)
        return false
      }
      clearProactiveRefresh()
      // Nettoyage local sans appel API pour éviter boucle interceptor 401
      set({ user: null, accessToken: null, isAuthenticated: false, sessionExpired: true })
      delete api.defaults.headers.common['Authorization']
      return false
    }
  },
}))

// Quand l'onglet redevient visible après une période d'inactivité,
// déclencher un refresh préventif pour éviter un 401 sur la prochaine action.
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'visible') {
    const { isAuthenticated, accessToken, refreshAccessToken } = useAuthStore.getState()
    if (isAuthenticated && accessToken) {
      refreshAccessToken()
    }
  }
})

export { useAuthStore }
